<template>
  <AppDialog
    class="insights-performance-highlights-dialog"
    append-to-body
    is-dismissable
    :show="show"
    @close="close"
  >
    <div class="header">
      <AppAvatarInitial
        v-if="highlightsData.type === 'user'"
        :text="highlightsData.name"
        size="speck"
      />
      <span
        class="team-member-name"
        @click="onNameClick"
      >
        {{ highlightsData.name }}
      </span>
      <button
        class="close"
        data-dismiss="modal"
        aria-hidden="true"
        type="button"
        @click="close"
      >
        <i class="hi hi-close hi-12"></i>
      </button>
    </div>
    <div class="description">
      {{ description }}
    </div>
    <div class="table-wrapper">
      <AppTable :table-data="tableData">
        <AppTableColumn
          v-slot="{ row }"
          name="metric"
          header="Metric"
        >
          {{ row.metricText }}
        </AppTableColumn>
        <AppTableColumn
          v-slot="{ row }"
          name="avg"
          :header-classes="['text-align-right']"
          :header="`Avg. last ${timeFrameLabel}`"
          header-tooltip="Average per member / day"
          header-tooltip-type="info-filled"
        >
          <div class="metric">
            <span class="current-value">
              {{ getValue({ row, period: 'current_value' }) }}
            </span>
            <div v-if="row.change">
              <i
                class="hi hi-right-arrow"
                :style="getArrowStyle(row)"
              ></i>
              <span :style="getPercentageStyle(row)">
                {{ getPercentage(row) }}
              </span>
            </div>
          </div>
        </AppTableColumn>
        <AppTableColumn
          v-slot="{ row }"
          name="prev_avg"
          :header-classes="['text-align-right']"
          header="Previous period avg."
          header-tooltip="Average per member / day"
          header-tooltip-type="info-filled"
        >
          <div class="d-flex justify-content-end">
            {{ getValue({ row, period: 'prev_value' }) }}
          </div>
        </AppTableColumn>
      </AppTable>
    </div>
  </AppDialog>
</template>

<script>
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import updateLocale from 'dayjs/plugin/updateLocale';
import en from 'dayjs/locale/en';
import AppAvatarInitial from '~/components/AppAvatarInitial.vue';
import { INSIGHTS } from '~/app_constants.js';
import { formatDuration } from '~/utils/time_utils';

export default {
  name: 'InsightsPerformanceHighlightsDialog',
  components: {
    AppAvatarInitial,
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    highlightsData: {
      type: Object,
      required: true,
    },
    currentRange: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    loading: false,
  }),
  computed: {
    timeFrameLabel() {
      return this.currentRange.label.includes('month') ? 'month' : 'week';
    },
    description() {
      return `Comparing averages from last ${this.timeFrameLabel} against the period of the previous 3 ${
        this.timeFrameLabel
      }s
      (${this.computeDates(this.currentRange?.id || 'this-month')}).`;
    },
    computedTimeFrame() {
      return ``;
    },
    tableData() {
      return this.highlightsData.cards.map((item) => ({
        ...item,
        metricText: INSIGHTS.performanceHighlights.labels.metric[item.metric],
      }));
    },
  },
  created() {
    dayjs.extend(weekday);
    dayjs.extend(weekOfYear);
    dayjs.extend(updateLocale);
    dayjs.updateLocale('en', {
      ...en,
      weekStart: 1,
    });
  },
  methods: {
    getArrowStyle(highlight) {
      const direction = Math.sign(highlight.change) === 1 ? 'ascending' : 'descending';

      return INSIGHTS.performanceHighlights.styles[direction].arrow;
    },
    getPercentage(highlight) {
      return `${Math.abs(highlight.change)}%`;
    },
    getPercentageStyle(highlight) {
      const direction = Math.sign(highlight.change) === 1 ? 'ascending' : 'descending';

      return INSIGHTS.performanceHighlights.styles[direction].text;
    },
    getValue({ row, period }) {
      if (row[period] === null) {
        return '-';
      }

      // Period can be either current_value or previous_value
      return row.metric === 'work_time' ? formatDuration(row[period], { showSeconds: 'never' }) : `${row[period]}%`;
    },
    computeDates(range) {
      return range.includes('month') ? this.getPreviousMonths() : this.getPreviousWeeks();
    },
    getPreviousWeeks() {
      const currentWeekOfYearIndex = dayjs().week();
      const fromDate = dayjs()
        .week(currentWeekOfYearIndex - 3)
        .startOf('week')
        .format('MMM D, YYYY');
      const toDate = dayjs()
        .week(currentWeekOfYearIndex - 1)
        .endOf('week')
        .format('MMM D, YYYY');

      return `${fromDate} - ${toDate}`;
    },
    getPreviousMonths() {
      const currentMonthIndex = dayjs().month();
      const fromDate = dayjs()
        .month(currentMonthIndex - 4)
        .date(1)
        .format('MMM D, YYYY');
      const toDate = dayjs()
        .month(currentMonthIndex - 2)
        .endOf('month')
        .format('MMM D, YYYY');

      return `${fromDate} - ${toDate}`;
    },
    close() {
      this.$emit('close');
    },
    onNameClick() {
      const { id, type } = this.highlightsData;

      this.$emit('close', { id, type });
    },
  },
};
</script>
